import { notify } from '@billy/design-system'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { RecurringInvoiceDraft } from '@features/recurringInvoices/types/recurringInvoiceDraft'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { queryClient } from '../../../../../../config/queryClient'
import { useDirtyRoute } from '../../../../../../contexts/dirtyRouteContext'
import { NotificationKeys } from '../../../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../../../enums/queryKeys'
import { useSegment } from '../../../../../../hooks'
import { getOrganizationViewUrl } from '../../../../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../../../../utils/routing/reactRoute'
import { useInvoiceEditorForm } from '../../contexts/invoiceEditorFormContext'
import { useRecurringInvoiceForm } from '../../contexts/recurringInvoiceFormContext'
import { useInvoiceDraftPayload } from '../useInvoiceDraftPayload'
import { useInvoiceEditorDocumentType } from '../useInvoiceEditorDocumentType'
import { useCreateRecurringInvoiceDraft } from './useCreateRecurringInvoiceDraft'
import { useRecurringInvoicePayload } from './useRecurringInvoicePayload'
import { useUpdateRecurringInvoiceDraft } from './useUpdateRecurringInvoiceDraft'

export interface SaveRecurringInvoiceDraftOptions {
  onError?: () => void
  onSuccess?: (recurringInvoice: RecurringInvoiceDraft) => void
}

export const useSaveRecurringInvoiceDraft = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { track } = useSegment()

  const { setDirty } = useDirtyRoute()
  const { organization } = useUserOrganization()
  const { getInvoiceDraftPayload, isLoading } = useInvoiceDraftPayload()
  const { getRecurringInvoiceDraftPayload } = useRecurringInvoicePayload()
  const { createRecurringInvoiceDraft, isProcessing: isProcessingCreateRecurringInvoiceDraft } =
    useCreateRecurringInvoiceDraft()
  const { updateRecurringInvoiceDraft, isProcessing: isProcessingUpdateRecurringInvoiceDraft } =
    useUpdateRecurringInvoiceDraft()
  const { documentType } = useInvoiceEditorDocumentType()
  const { recurringInvoiceForm } = useRecurringInvoiceForm()
  const { invoiceEditorForm } = useInvoiceEditorForm()

  const { getValues: getInvoiceValues } = invoiceEditorForm
  const { getValues: getRecurringInvoiceValues } = recurringInvoiceForm

  const isProcessing = isProcessingCreateRecurringInvoiceDraft || isProcessingUpdateRecurringInvoiceDraft

  const saveRecurringInvoiceDraft = useCallback(
    ({ onError, onSuccess }: SaveRecurringInvoiceDraftOptions = {}) => {
      const invoiceFormData = getInvoiceValues()
      const recurringInvoiceFormValues = getRecurringInvoiceValues()
      const invoiceDraftPayload = getInvoiceDraftPayload(documentType, invoiceFormData)
      const recurringInvoicePayload = getRecurringInvoiceDraftPayload(invoiceDraftPayload, recurringInvoiceFormValues)
      const isSavingNewDraft = recurringInvoicePayload?.id === undefined // if we have the id, we're editing an existing recurring invoice
      const saveRecurringInvoiceFn = isSavingNewDraft ? createRecurringInvoiceDraft : updateRecurringInvoiceDraft

      saveRecurringInvoiceFn(
        { payload: recurringInvoicePayload },
        {
          onError: () => {
            notify({
              id: NotificationKeys.RecurringInvoiceSavedAsDraft,
              message: t('error_saving'),
              variant: 'error',
            })
            onError?.()
          },
          onSuccess: (draftSaveResponse) => {
            setDirty(false)

            if (onSuccess) {
              onSuccess(draftSaveResponse?.data)
            } else {
              const recurringInvoiceId = String(draftSaveResponse?.data?.id)
              queryClient.invalidateQueries([QueryKeys.RecurringInvoice, recurringInvoiceId])
              queryClient.invalidateQueries([QueryKeys.RecurringInvoiceDraftFormat, recurringInvoiceId])
              queryClient.invalidateQueries([QueryKeys.RecurringInvoices])
              queryClient.invalidateQueries([QueryKeys.RecurringInvoicesSummary])

              if (isSavingNewDraft) {
                track('XXX - Invoicing Core - Recurring invoice saved as draft')
              }

              notify({
                id: NotificationKeys.RecurringInvoiceSavedAsDraft,
                message: t('recurring_invoices.save_draft.notification.success'),
                variant: 'success',
              })

              navigate(
                `${getOrganizationViewUrl(
                  organization?.url,
                  reactRoute.organization.recurringInvoices.getMainRoute(),
                )}`,
              )
            }
          },
        },
      )
    },
    [
      createRecurringInvoiceDraft,
      documentType,
      getInvoiceDraftPayload,
      getInvoiceValues,
      getRecurringInvoiceDraftPayload,
      getRecurringInvoiceValues,
      navigate,
      organization?.url,
      setDirty,
      t,
      track,
      updateRecurringInvoiceDraft,
    ],
  )

  return { saveRecurringInvoiceDraft, isLoading, isSaving: isProcessing }
}
